import React from 'react';
import Select from 'react-select';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';
import ColorPicker from './ColorPicker';
import LabelColorSlider from './LabelColorSlider';
import {
  ASSET_STATUS_ANIMATIONS,
  STATUS_RULE_OPERATORS,
  findDatastreamMeta,
} from '../widgets/assetStatusUtils';

const toggleStyle = { color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' };

const fieldCardStyle = {
  width: '100%',
  padding: 12,
  borderRadius: 10,
  border: '1px solid rgba(79, 196, 211, 0.18)',
  background: 'rgba(5, 17, 35, 0.38)',
};

const buildDatastreamOptions = (datastreams = []) => (datastreams || [])
  .filter(Boolean)
  .map((ds) => ({
    label: `${ds.name || ds.pin || 'Datastream'}${ds.pin ? ` [${ds.pin}]` : ''}`,
    value: ds.id,
    pin: ds.pin,
    datastream: ds,
  }));

const getAvailableDatastreams = ({ edit, devices, validDatastreams }) => {
  if (Array.isArray(devices) && devices.length > 0) {
    const selectedDevice = devices.find((device) => Number(device?.id) === Number(edit.deviceId));
    return (selectedDevice?.template?.datastreams || []).filter(Boolean);
  }
  return (validDatastreams || []).filter(Boolean);
};

const selectValueFor = (options, dataId, pin) => (
  options.find((option) => String(option.value) === String(dataId)) ||
  options.find((option) => String(option.pin || '') === String(pin || '')) ||
  null
);

const buildSelectedRowOptions = (rows, availableDatastreams) => (rows || [])
  .map((row, index) => {
    const ds = findDatastreamMeta(availableDatastreams, row?.dataId, row?.pin);
    if (!ds) return null;
    return {
      label: `${ds.name || row?.label || `Row ${index + 1}`} [${ds.pin}]`,
      value: String(ds.id ?? ds.pin),
      dataId: ds.id,
      pin: ds.pin,
      datastream: ds,
    };
  })
  .filter(Boolean);

const getMinMaxForDatastream = (datastream) => {
  const min = Number(datastream?.min);
  const max = Number(datastream?.max);
  return {
    min: Number.isFinite(min) ? min : 0,
    max: Number.isFinite(max) ? max : 100,
  };
};

const RuleEditor = ({ rules = [], disabled, onChange }) => {
  const nextRules = Array.isArray(rules) ? rules : [];
  const updateRule = (index, patch) => {
    onChange(nextRules.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule)));
  };
  const removeRule = (index) => {
    onChange(nextRules.filter((_, ruleIndex) => ruleIndex !== index));
  };

  return (
    <div className="form-item" style={{ width: '100%' }}>
      <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
        Icon conditional colors and animation
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {nextRules.map((rule, index) => {
          const operator = rule.operator || 'eq';

          return (
            <div key={`asset-icon-rule-${index}`} style={fieldCardStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.8fr)', gap: 10 }}>
                <div className="form-item" style={{ marginBottom: 0 }}>
                  <label className="form-lbl" style={{ color: 'var(--header-color)' }}>When value is</label>
                  <Select
                    classNamePrefix="react-select"
                    options={STATUS_RULE_OPERATORS}
                    value={STATUS_RULE_OPERATORS.find((option) => option.value === operator) || STATUS_RULE_OPERATORS[1]}
                    onChange={(option) => updateRule(index, { operator: option?.value || 'eq' })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr)) 88px', gap: 10, marginTop: 10, alignItems: 'end' }}>
                {operator === 'eq' ? (
                  <div className="form-item" style={{ marginBottom: 0 }}>
                    <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Equals</label>
                    <input
                      className="add-column"
                      placeholder="RUNNING / OFF / 1"
                      value={rule.equals ?? ''}
                      onChange={(event) => updateRule(index, { equals: event.target.value })}
                    />
                  </div>
                ) : operator === 'lte' ? (
                  <div className="form-item" style={{ marginBottom: 0 }}>
                    <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Less / equal</label>
                    <input
                      className="add-column"
                      type="number"
                      placeholder="100"
                      value={rule.to ?? ''}
                      onChange={(event) => updateRule(index, { to: event.target.value })}
                    />
                  </div>
                ) : (
                  <div className="form-item" style={{ marginBottom: 0 }}>
                    <label className="form-lbl" style={{ color: 'var(--header-color)' }}>
                      {operator === 'gte' ? 'Greater / equal' : 'From'}
                    </label>
                    <input
                      className="add-column"
                      type="number"
                      placeholder="0"
                      value={rule.from ?? ''}
                      onChange={(event) => updateRule(index, { from: event.target.value })}
                    />
                  </div>
                )}

                {operator === 'range' ? (
                  <div className="form-item" style={{ marginBottom: 0 }}>
                    <label className="form-lbl" style={{ color: 'var(--header-color)' }}>To</label>
                    <input
                      className="add-column"
                      type="number"
                      placeholder="100"
                      value={rule.to ?? ''}
                      onChange={(event) => updateRule(index, { to: event.target.value })}
                    />
                  </div>
                ) : (
                  <div className="form-item" style={{ marginBottom: 0 }} />
                )}

                <div className="form-item" style={{ marginBottom: 0 }}>
                  <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Color</label>
                  <ColorPicker
                    id={`asset-icon-rule-${index}-color`}
                    color={rule.color || '#00d26a'}
                    setColor={(color) => updateRule(index, { color })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 78px', gap: 10, marginTop: 10, alignItems: 'end' }}>
                <div className="form-item" style={{ marginBottom: 0 }}>
                  <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Animation</label>
                  <Select
                    classNamePrefix="react-select"
                    options={ASSET_STATUS_ANIMATIONS}
                    value={ASSET_STATUS_ANIMATIONS.find((option) => option.value === (rule.animation || 'none')) || ASSET_STATUS_ANIMATIONS[0]}
                    onChange={(option) => updateRule(index, { animation: option?.value || 'none' })}
                  />
                </div>
                <button type="button" className="btn cancel-btn" onClick={() => removeRule(index)}>
                  Remove
                </button>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          className="btn add-btn"
          disabled={disabled}
          onClick={() => onChange([
            ...nextRules,
            {
              operator: 'eq',
              equals: 'ON',
              color: '#00d26a',
              animation: 'pulse',
            },
          ])}
        >
          Add icon rule
        </button>
      </div>
    </div>
  );
};

const AssetStatusSettings = ({
  edit,
  setEdit,
  devices,
  validDatastreams,
  deviceOptions,
}) => {
  const availableDatastreams = getAvailableDatastreams({ edit, devices, validDatastreams });
  const datastreamOptions = buildDatastreamOptions(availableDatastreams);
  const updateProps = (patch) => setEdit((prev) => ({
    ...prev,
    props: { ...(prev.props || {}), ...patch },
  }));
  const rows = Array.isArray(edit.props?.rows) ? edit.props.rows : [];
  const rowOptions = buildSelectedRowOptions(rows, availableDatastreams);
  const iconSource = (
    selectValueFor(rowOptions, edit.props?.iconDataId, edit.props?.iconPin) ||
    selectValueFor(rowOptions, edit.props?.iconGradientDataId, edit.props?.iconGradientPin) ||
    rowOptions[0] ||
    null
  );
  const iconBounds = getMinMaxForDatastream(iconSource?.datastream);
  const updateIconSource = (option) => updateProps({
    iconDataId: option?.datastream?.id ?? null,
    iconPin: option?.datastream?.pin ?? '',
    iconGradientDataId: option?.datastream?.id ?? null,
    iconGradientPin: option?.datastream?.pin ?? '',
  });

  return (
    <>
      {Array.isArray(devices) && devices.length > 0 ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Device</label>
          <Select
            classNamePrefix="react-select"
            options={deviceOptions}
            value={deviceOptions.find((device) => Number(device.value) === Number(edit.deviceId)) || null}
            onChange={(option) => setEdit((prev) => ({
              ...prev,
              deviceId: option ? Number(option.value) : 0,
              props: {
                ...(prev.props || {}),
                rows: [],
                iconDataId: null,
                iconPin: '',
                iconGradientDataId: null,
                iconGradientPin: '',
                iconRules: [],
              },
            }))}
            isClearable
          />
        </div>
      ) : null}

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Asset name</label>
        <input
          className="add-column"
          value={edit.props?.assetName ?? ''}
          placeholder="Name of Assets"
          onChange={(event) => updateProps({ assetName: event.target.value })}
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Displayed datastream rows</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((row, index) => {
            const rowDatastream = findDatastreamMeta(availableDatastreams, row.dataId, row.pin);
            return (
              <div
                key={`asset-row-${index}`}
                style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(120px, 0.8fr) 42px', gap: 8 }}
              >
                <Select
                  classNamePrefix="react-select"
                  options={datastreamOptions}
                  value={selectValueFor(datastreamOptions, row.dataId, row.pin)}
                  onChange={(option) => {
                    const nextRows = rows.map((item, rowIndex) => rowIndex === index
                      ? {
                        ...item,
                        dataId: option?.datastream?.id ?? null,
                        pin: option?.datastream?.pin ?? '',
                        label: option?.datastream?.name || '',
                      }
                      : item);
                    updateProps({ rows: nextRows });
                  }}
                  isClearable
                />
                <input
                  className="add-column"
                  placeholder={rowDatastream?.name || 'Label'}
                  value={rowDatastream?.name || row.label || ''}
                  disabled={Boolean(rowDatastream)}
                  onChange={(event) => {
                    const nextRows = rows.map((item, rowIndex) => rowIndex === index
                      ? { ...item, label: event.target.value }
                      : item);
                    updateProps({ rows: nextRows });
                  }}
                />
                <button
                  type="button"
                  className="btn cancel-btn"
                  onClick={() => updateProps({ rows: rows.filter((_, rowIndex) => rowIndex !== index) })}
                >
                  x
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="btn add-btn"
            onClick={() => updateProps({ rows: [...rows, { dataId: null, pin: '', label: '', showUnit: true }] })}
          >
            Add datastream row
          </button>
        </div>
      </div>

      <div className="form-item" style={fieldCardStyle}>
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Icon datastream behavior</label>
        <Select
          classNamePrefix="react-select"
          options={rowOptions}
          value={iconSource}
          onChange={updateIconSource}
          placeholder="Choose one displayed row"
          isClearable
        />
      </div>

      <div className="form-item" style={fieldCardStyle}>
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Icon gradient by value</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {edit.props?.iconGradientEnabled ? (
            <ToggleOn style={toggleStyle} onClick={() => updateProps({ iconGradientEnabled: false })} />
          ) : (
            <ToggleOff style={toggleStyle} onClick={() => updateProps({ iconGradientEnabled: true })} />
          )}
          <strong style={{ color: 'var(--header-color)' }}>
            {iconSource ? iconSource.label : 'Choose one icon datastream above'}
          </strong>
          <label className="form-lbl" style={{ color: 'var(--header-color)', display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            Smooth
            <input
              className="checkbox-input"
              type="checkbox"
              checked={edit.props?.iconGradientColorGrading !== false}
              onChange={(event) => updateProps({ iconGradientColorGrading: event.target.checked })}
            />
          </label>
        </div>
        {edit.props?.iconGradientEnabled ? (
          <LabelColorSlider
            minValue={iconBounds.min}
            maxValue={iconBounds.max}
            stops={edit.props?.iconGradientColors || []}
            colorGrading={edit.props?.iconGradientColorGrading !== false}
            setStops={(updated) => updateProps({ iconGradientColors: updated })}
          />
        ) : null}
      </div>

      <RuleEditor
        rules={edit.props?.iconRules}
        disabled={!iconSource}
        onChange={(iconRules) => updateProps({ iconRules })}
      />

      <div className="form-item" style={fieldCardStyle}>
        <label className="form-lbl" style={{ color: 'var(--header-color)' }}>Border follows icon color</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {edit.props?.borderEnabled ? (
            <ToggleOn style={toggleStyle} onClick={() => updateProps({ borderEnabled: false })} />
          ) : (
            <ToggleOff style={toggleStyle} onClick={() => updateProps({ borderEnabled: true })} />
          )}
          <input
            className="add-column"
            type="number"
            min="1"
            max="12"
            style={{ width: 110 }}
            value={edit.props?.borderWidth ?? 1}
            onChange={(event) => updateProps({ borderWidth: event.target.value })}
          />
        </div>
      </div>
    </>
  );
};

export default AssetStatusSettings;
